import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output } from '@angular/core';

export interface AdminTabOption {
  id: string;
  label: string;
  description?: string;
  icon?: string;
  tabId?: string;
  panelId?: string;
}

@Component({
  selector: 'app-admin-tabs',
  standalone: true,
  imports: [CommonModule],
  host: { '[class.admin-tabs-compact]': 'compact' },
  template: `
    <nav class="admin-tabs" role="tablist" [attr.aria-label]="ariaLabel">
      <button
        *ngFor="let tab of tabs; trackBy: trackTab"
        type="button"
        role="tab"
        class="admin-tab"
        [class.active]="active === tab.id"
        [class.no-icon]="!tab.icon"
        [attr.aria-selected]="active === tab.id"
        [attr.aria-controls]="tab.panelId || null"
        [attr.id]="tab.tabId || null"
        (click)="tabSelect.emit(tab.id)"
      >
        <i *ngIf="tab.icon" class="bi" [ngClass]="tab.icon" aria-hidden="true"></i>
        <span class="tab-copy">
          <strong>{{ tab.label }}</strong>
          <small *ngIf="tab.description">{{ tab.description }}</small>
        </span>
      </button>
    </nav>
  `,
  styles: [`
    :host{display:block;width:fit-content;max-width:100%}
    .admin-tabs{display:flex;align-items:flex-end;width:fit-content;max-width:100%;padding:0 .3rem;border-bottom:1px solid var(--ts-border-strong);background:transparent;flex-wrap:wrap;gap:.15rem}
    .admin-tab{position:relative;display:grid;grid-template-columns:28px minmax(0,1fr);align-items:center;gap:.5rem;flex:0 0 auto;min-width:200px;max-width:340px;padding:.7rem 1.05rem;color:var(--ts-text-muted);text-align:left;background:transparent;border:1px solid transparent;border-bottom:0;border-radius:10px 10px 0 0;cursor:pointer;transition:color var(--ts-transition),background var(--ts-transition),border-color var(--ts-transition)}
    .admin-tab.no-icon{grid-template-columns:minmax(0,1fr)}
    :host(.admin-tabs-compact) .admin-tab{min-width:0;max-width:none;padding:.5rem .85rem}
    :host(.admin-tabs-compact) .tab-copy strong{font-size:.76rem}
    :host(.admin-tabs-compact) .tab-copy small{font-size:.6rem}
    .admin-tab::after{content:'';position:absolute;right:.7rem;bottom:-1px;left:.7rem;height:3px;border-radius:3px 0 0;background:linear-gradient(90deg,var(--ts-primary),var(--ts-indigo));transform:scaleX(0);transform-origin:center;transition:transform .2s ease}
    .admin-tab:hover{color:var(--ts-text);background:color-mix(in srgb, var(--ts-primary) 14%, transparent)}
    .admin-tab:focus-visible{z-index:1;outline:3px solid color-mix(in srgb, var(--ts-primary) 42%, transparent);outline-offset:-3px}
    .admin-tab>i{display:grid;place-items:center;width:28px;height:28px;color:var(--ts-text-subtle);border-radius:7px;font-size:.9rem;transition:color var(--ts-transition),background var(--ts-transition)}
    .admin-tab.active{z-index:1;color:var(--ts-text);background:var(--ts-surface);border-color:var(--ts-border-strong)}
    .admin-tab.active::after{transform:scaleX(1)}
    .admin-tab.active>i{color:var(--ts-primary);background:color-mix(in srgb, var(--ts-primary) 16%, transparent)}
    .tab-copy{display:grid;gap:.12rem;min-width:0}.tab-copy strong{color:inherit;font-size:.82rem;line-height:1.2;white-space:normal}.tab-copy small{color:var(--ts-text-subtle);font-size:.64rem;line-height:1.3;white-space:normal}.admin-tab.active .tab-copy small{color:var(--ts-text-muted)}
    @media(max-width:700px){.admin-tab{min-width:170px;padding:.62rem .8rem}.tab-copy small{white-space:normal}}
    @media(max-width:560px){:host,.admin-tabs{width:100%}.admin-tabs{display:grid;grid-auto-flow:column;grid-auto-columns:minmax(0,1fr);padding:0}.admin-tab{display:flex;justify-content:center;gap:.35rem;min-width:0;padding:.55rem .35rem;text-align:center}.admin-tab>i{flex:0 0 24px;width:24px;height:24px}.tab-copy strong{font-size:.7rem;white-space:normal}.tab-copy small{display:none}}
    @media(max-width:600px){.admin-tab::after{right:.28rem;left:.28rem;height:2px}.admin-tab>i{display:none}.tab-copy strong{font-size:.66rem}}
    @media(prefers-reduced-motion:reduce){.admin-tab,.admin-tab::after,.admin-tab>i{transition:none}}
  `]
})
export class AdminTabsComponent {
  @Input() tabs: readonly AdminTabOption[] = [];
  @Input() active = '';
  @Input() ariaLabel = 'Secciones';
  @Input() compact = false;
  @Output() tabSelect = new EventEmitter<string>();

  trackTab(_: number, tab: AdminTabOption): string {
    return tab.id;
  }
}
